import { Link, createFileRoute } from '@tanstack/react-router';
import { useShallow } from 'zustand/react/shallow';
import { useTranslation } from 'react-i18next';
import dayjs from 'dayjs';
import { Check } from 'lucide-react';
import { t as ts } from '@/lib/i18n';
import orderStore from '@/stores/order.store.ts';
import { OrderDefaults } from '@/lib/types/order.types.ts';
import Section from '@/components/layout/section.tsx';

const { VITE_LOCALE } = import.meta.env;

export const Route = createFileRoute('/order-success')({
	component: OrderSuccess,
	head: () => ({
		meta: [{ title: `Jervice - ${ts('order.success')}` }],
		links: [
			{
				rel: 'stylesheet',
				href: 'https://fonts.googleapis.com/css2?family=Space+Mono:ital,wght@0,400;0,700;1,400;1,700&display=swap',
			},
		],
	}),
});

function OrderSuccess() {
	const { t } = useTranslation();

	const lastOrder = orderStore(useShallow((state) => state.lastOrder));

	const currencyFormatter = new Intl.NumberFormat(VITE_LOCALE, {
		style: 'currency',
		currency: VITE_LOCALE === 'pl' ? 'PLN' : 'PLN',
	});

	const total = lastOrder?.services?.reduce((acc, item) => acc + item?.price, 0);
	const totalFormatted = currencyFormatter.format(total || 0);

	const getTranslation = (value?: string | number) => {
		if (typeof value === 'number') {
			if (value === 1900) {
				return t('order.not_specified');
			} else {
				return value;
			}
		} else {
			if (value === OrderDefaults.NotSpecified) {
				return t('order.not_specified');
			} else {
				return value;
			}
		}
	};

	return (
		<Section className="w-full h-[calc(100svh_-_166px)] max-lg:h-auto flex max-lg:flex-col py-6 max-md:py-4">
			<div className="w-[calc(100%_-_400px)] max-w-[800px] h-full flex flex-col items-center justify-center p-6 max-sm:p-4 gap-6 m-auto max-lg:w-full">
				<div className="flex items-center justify-center w-32 h-32 max-md:w-24 max-md:h-24 rounded-full bg-green-700">
					<Check className="text-white" size="4rem" />
				</div>

				<h2 className="text-5xl max-md:text-3xl text-neutral-600">{t('order.success')}</h2>

				<p className="text-2xl max-md:text-xl text-neutral-600 max-w-[500px] text-center">
					{t('order.success_text')}
				</p>

				<Link
					to="/"
					className="py-2 px-4 text-lg my-2 rounded-xl bg-[var(--primary-light)] text-[var(--primary)] font-medium hover:brightness-95 cursor-pointer active:scale-97"
				>
					{t('order_service.back_home')}
				</Link>
			</div>

			<div className="w-[400px] h-full max-lg:w-full mono">
				<div className="w-[400px] mx-auto h-full max-w-[100%] overflow-y-auto shadow-md rounded-lg bg-neutral-50 p-4 flex flex-col gap-2 flex-1">
					<div className="w-[120px] grayscale-100 mx-auto">
						<img src={`/logo-${VITE_LOCALE}.svg`} alt="jervice-logo" />
					</div>

					<div className="dashed-line my-2" />

					<span className="text-center uppercase text-xl font-medium text-neutral-500 tracking-wider">
						{t('order.invoice.title', { order: lastOrder?.response?.id || '__' })}
					</span>

					<div className="dashed-line my-2" />

					<span className="font-medium text-sm uppercase text-neutral-500 tracking-wider">
						{t('order.invoice.client')}
					</span>

					<div className="flex items-center gap-2 justify-between text-sm">
						<p className="text-sm">{t('order.placeholders.name')}</p>
						<p className="max-w-[140px] truncate">{lastOrder?.payload?.fullName || '__________'}</p>
					</div>

					<div className="flex items-center gap-2 justify-between text-sm">
						<p className="text-sm">{t('order.placeholders.phone')}</p>
						<p className="max-w-[140px] truncate">
							{lastOrder?.payload?.phoneNumber || '__________'}
						</p>
					</div>

					<div className="flex items-center gap-2 justify-between text-sm">
						<p className="text-sm">{t('order.placeholders.email')}</p>
						<p className="max-w-[140px] truncate">{lastOrder?.payload?.email || '__________'}</p>
					</div>

					<div className="dashed-line my-2" />

					<span className="font-medium text-sm uppercase text-neutral-500 tracking-wider">
						{t('order.invoice.car')}
					</span>

					<div className="flex items-center gap-2 justify-between text-sm">
						<p className="text-sm">{t('order.placeholders.brand')}</p>
						<p className="max-w-[140px] truncate">
							{getTranslation(lastOrder?.payload?.vehicleBrand) || '__________'}
						</p>
					</div>

					<div className="flex items-center gap-2 justify-between text-sm">
						<p className="text-sm">{t('order.placeholders.model')}</p>
						<p className="max-w-[140px] truncate">
							{getTranslation(lastOrder?.payload?.vehicleModel) || '__________'}
						</p>
					</div>

					<div className="flex items-center gap-2 justify-between text-sm">
						<p className="text-sm">{t('order.placeholders.year')}</p>
						<p className="max-w-[140px] truncate">
							{getTranslation(lastOrder?.payload?.carYear) || '__________'}
						</p>
					</div>

					<div className="flex items-center gap-2 justify-between text-sm">
						<p className="text-sm">{t('order.placeholders.license')}</p>
						<p className="max-w-[140px] truncate">
							{getTranslation(lastOrder?.payload?.carLicense) || '__________'}
						</p>
					</div>

					<div className="dashed-line my-2" />

					<span className="font-medium text-sm uppercase text-neutral-500 tracking-wider">
						{t('order.invoice.services')}
					</span>

					{lastOrder?.services?.map((service) => (
						<div className="flex items-start gap-2 justify-between text-sm" key={service?.id}>
							<div className="flex flex-col gap-1">
								<p className="text-sm">{service?.name}</p>
								<span className="capitalize text-xs text-neutral-400">{`${dayjs(service?.date?.day).locale(VITE_LOCALE).format('MMMM, ddd DD')} • ${service?.date?.slot?.startTime}`}</span>
							</div>
							<p className="max-w-[140px] truncate">
								{t('services.sub_service_start_from', {
									qty: currencyFormatter.format(service?.price),
								})}
							</p>
						</div>
					))}

					<div className="dashed-line my-2" />

					<div className="flex items-center gap-2 justify-between text-sm">
						<span className="font-medium text-sm uppercase text-neutral-500 tracking-wider">
							{t('order.total')}
						</span>

						<p className="max-w-[140px] truncate">
							{t('services.sub_service_start_from', { qty: totalFormatted })}
						</p>
					</div>
				</div>
			</div>
		</Section>
	);
}
